import Papa from "papaparse";

import { UrlMappingMatchType } from "~/src/__generated__/schema";
import { downloadCsvWithBom } from "~/src/lib/utils";

export const IMPORT_MAX_URL_MAPPINGS = 500;

export const CSV_HEADERS = [
  "name",
  "active",
  "match_type",
  "priority",
  "source_url",
  "source_host",
  "source_path",
  "regex_pattern",
  "destination_url",
  "destination_mode",
  "condition_path_contains",
  "condition_path_prefix",
  "condition_path_excludes",
  "condition_query_param_key",
  "condition_query_param_exact",
  "condition_query_param_values",
  "condition_query_contains",
  "condition_query_excludes",
  "condition_referrer_contains",
  "condition_title_equals",
  "condition_environments",
  "conditions_json",
] as const;

/** Example rows included in the downloadable CSV import template. */
export const TEMPLATE_EXAMPLE_ROWS: readonly string[][] = [
  [
    "Example - exact single",
    "true",
    "exact",
    "10",
    "https://example.com/lp/?utm_content=source_a",
    "",
    "",
    "",
    "https://example.com/lp/?utm_content=mapped_a",
    "static",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
  ],
  [
    "Example - exact multi",
    "true",
    "exact",
    "10",
    "https://example.com/a|https://example.com/b",
    "",
    "",
    "",
    "https://example.com/to",
    "static",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
  ],
  [
    "Example - prefix",
    "true",
    "prefix",
    "20",
    "https://example.com/campaign/",
    "",
    "",
    "",
    "https://example.com/campaign/mapped",
    "static",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
  ],
  [
    "Example - regex",
    "true",
    "regex",
    "30",
    "",
    "",
    "",
    "^https://example\\.com/item/(\\d+)$",
    "https://example.com/products/\\1",
    "static",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
  ],
  [
    "Example - host_path",
    "true",
    "host_path",
    "40",
    "",
    "gateway.example.com",
    "/landing*",
    "",
    "https://shop.example.com/lp/?u=mapped",
    "static",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
  ],
  [
    "Example - transform single p",
    "true",
    "transform",
    "100",
    "",
    "",
    "",
    "",
    "https://example.com/shopping/lp/mapped{query_string}",
    "template",
    "example.com|shopping/lp",
    "",
    "",
    "p",
    "sample_product",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
  ],
  [
    "Example - transform multi p",
    "true",
    "transform",
    "100",
    "",
    "",
    "",
    "",
    "https://example.com/shopping/lp/hidden{query_string}",
    "template",
    "example.com|shopping/lp",
    "",
    "",
    "p",
    "",
    "product_a|product_b|product_c",
    "",
    "",
    "",
    "",
    "",
    "",
  ],
  [
    "Example - production only",
    "true",
    "exact",
    "50",
    "https://example.com/prod-page",
    "",
    "",
    "",
    "https://example.com/mapped",
    "static",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "production",
    "",
  ],
  [
    "Example - conditions_json",
    "true",
    "transform",
    "60",
    "",
    "",
    "",
    "",
    "https://example.com/plan{query_string}",
    "template",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    '{"path_contains":["/plan"],"title_equals":["見積り結果"]}',
  ],
];

export function buildTemplateCsv(): string {
  return Papa.unparse({
    fields: [...CSV_HEADERS],
    data: TEMPLATE_EXAMPLE_ROWS.map((row) => [...row]),
  });
}

export function downloadImportTemplate(): void {
  downloadCsvWithBom("URLマッピングインポートテンプレート", buildTemplateCsv());
}

const normalizeHeaderCell = (raw: string | undefined): string =>
  (raw ?? "")
    .replace(/^\uFEFF/, "")
    .trim()
    .toLowerCase();

const csvBoolean = (value: string | undefined): boolean => {
  const t = (value ?? "").trim().toLowerCase();
  return t === "true" || t === "1";
};

const emptyToNull = (value: string | undefined): string | null => {
  const t = (value ?? "").trim();
  return t === "" ? null : t;
};

const parseMatchType = (raw: string): UrlMappingMatchType | null => {
  const upper = raw.trim().toUpperCase();
  const allowed: UrlMappingMatchType[] = [
    "EXACT",
    "PREFIX",
    "REGEX",
    "HOST_PATH",
    "TRANSFORM",
  ];
  return allowed.includes(upper as UrlMappingMatchType)
    ? (upper as UrlMappingMatchType)
    : null;
};

export type UrlMappingImportRow = {
  name?: string | null;
  active?: boolean;
  matchType: UrlMappingMatchType;
  priority?: number | null;
  sourceUrl?: string | null;
  sourceHost?: string | null;
  sourcePath?: string | null;
  regexPattern?: string | null;
  destinationUrl: string;
  destinationMode?: "STATIC" | "TEMPLATE" | null;
  conditionPathContains?: string | null;
  conditionPathPrefix?: string | null;
  conditionPathExcludes?: string | null;
  conditionQueryParamKey?: string | null;
  conditionQueryParamExact?: string | null;
  conditionQueryParamValues?: string | null;
  conditionQueryContains?: string | null;
  conditionQueryExcludes?: string | null;
  conditionReferrerContains?: string | null;
  conditionTitleEquals?: string | null;
  conditionEnvironments?: string | null;
  conditionsJson?: string | null;
};

export type ParsedUrlMappingImportRow = {
  lineNum: number;
  row: UrlMappingImportRow;
  errors: string[];
};

export const parseCsvSourceUrls = (
  sourceUrl: string | null | undefined
): string[] => {
  if (!sourceUrl) return [];
  return sourceUrl
    .split(/[\n|]/)
    .map((value) => value.trim())
    .filter(Boolean);
};

export const validateImportRow = (
  row: UrlMappingImportRow,
  lineNum: number
): string[] => {
  const errors: string[] = [];
  const line = `${lineNum}行目`;

  switch (row.matchType) {
    case "EXACT":
      if (parseCsvSourceUrls(row.sourceUrl).length === 0) {
        errors.push(`${line}: source_url は必須です`);
      }
      break;
    case "PREFIX":
      if (!row.sourceUrl?.trim()) {
        errors.push(`${line}: source_url は必須です`);
      }
      break;
    case "REGEX":
      if (!row.regexPattern?.trim()) {
        errors.push(`${line}: regex_pattern は必須です`);
      }
      break;
    case "HOST_PATH":
      if (!row.sourceHost?.trim()) {
        errors.push(`${line}: source_host は必須です`);
      }
      if (!row.sourcePath?.trim()) {
        errors.push(`${line}: source_path は必須です`);
      }
      break;
    default:
      break;
  }

  if (row.conditionsJson) {
    try {
      const parsed: unknown = JSON.parse(row.conditionsJson);
      if (
        typeof parsed !== "object" ||
        parsed === null ||
        Array.isArray(parsed)
      ) {
        errors.push(
          `${line}: conditions_json はオブジェクトである必要があります`
        );
      }
    } catch {
      errors.push(`${line}: conditions_json のJSON形式が不正です`);
    }
  }

  return errors;
};

export const formatImportRowSourceSummary = (
  row: UrlMappingImportRow
): string => {
  switch (row.matchType) {
    case "EXACT": {
      const urls = parseCsvSourceUrls(row.sourceUrl);
      if (urls.length > 1) {
        return `${urls.length}件: ${urls[0]}`;
      }
      return row.sourceUrl || "-";
    }
    case "PREFIX":
      return row.sourceUrl || "-";
    case "REGEX":
      return row.regexPattern || "-";
    case "HOST_PATH":
      return [row.sourceHost, row.sourcePath].filter(Boolean).join("") || "-";
    case "TRANSFORM":
      return summarizeImportRowConditions(row);
    default:
      return "-";
  }
};

export const summarizeImportRowConditions = (
  row: UrlMappingImportRow
): string => {
  if (row.conditionsJson) {
    return "conditions_json";
  }

  const parts: string[] = [];
  if (row.conditionPathContains) parts.push("path_contains");
  if (row.conditionPathPrefix) parts.push("path_prefix");
  if (row.conditionPathExcludes) parts.push("path_excludes");
  if (row.conditionQueryParamKey) parts.push(`p=${row.conditionQueryParamKey}`);
  if (row.conditionQueryContains) parts.push("query_contains");
  if (row.conditionQueryExcludes) parts.push("query_excludes");
  if (row.conditionReferrerContains) parts.push("referrer_contains");
  if (row.conditionTitleEquals) parts.push("title_equals");
  if (row.conditionEnvironments) parts.push("environments");

  return parts.length > 0 ? parts.join(", ") : "(conditions)";
};

export const rowToImportInput = (row: UrlMappingImportRow) => ({
  name: row.name,
  active: row.active ?? true,
  matchType: row.matchType,
  priority: row.priority ?? 100,
  sourceUrl: row.sourceUrl,
  sourceHost: row.sourceHost,
  sourcePath: row.sourcePath,
  regexPattern: row.regexPattern,
  destinationUrl: row.destinationUrl,
  destinationMode: row.destinationMode,
  conditionPathContains: row.conditionPathContains,
  conditionPathPrefix: row.conditionPathPrefix,
  conditionPathExcludes: row.conditionPathExcludes,
  conditionQueryParamKey: row.conditionQueryParamKey,
  conditionQueryParamExact: row.conditionQueryParamExact,
  conditionQueryParamValues: row.conditionQueryParamValues,
  conditionQueryContains: row.conditionQueryContains,
  conditionQueryExcludes: row.conditionQueryExcludes,
  conditionReferrerContains: row.conditionReferrerContains,
  conditionTitleEquals: row.conditionTitleEquals,
  conditionEnvironments: row.conditionEnvironments,
  conditionsJson: row.conditionsJson,
});

const colsToRow = (
  header: string[],
  cols: string[],
  lineNum: number
): { row: UrlMappingImportRow } | { error: string } => {
  const get = (key: string): string | undefined => {
    const idx = header.indexOf(key);
    return idx >= 0 ? cols[idx] : undefined;
  };

  const matchTypeRaw = get("match_type");
  if (!matchTypeRaw?.trim()) {
    return { error: `${lineNum}行目: match_type は必須です` };
  }
  const matchType = parseMatchType(matchTypeRaw);
  if (!matchType) {
    return {
      error: `${lineNum}行目: match_type が不正です (${matchTypeRaw})`,
    };
  }

  const destinationUrl = emptyToNull(get("destination_url"));
  if (!destinationUrl) {
    return { error: `${lineNum}行目: destination_url は必須です` };
  }

  const destinationModeRaw = get("destination_mode")?.trim().toUpperCase();
  const destinationMode =
    destinationModeRaw === "TEMPLATE"
      ? "TEMPLATE"
      : destinationModeRaw === "STATIC"
      ? "STATIC"
      : null;

  const priorityRaw = get("priority")?.trim();
  const priority = priorityRaw ? Number(priorityRaw) : null;
  if (priorityRaw && Number.isNaN(priority)) {
    return { error: `${lineNum}行目: priority は数値で入力してください` };
  }

  return {
    row: {
      name: emptyToNull(get("name")),
      active: csvBoolean(get("active")),
      matchType,
      priority,
      sourceUrl: emptyToNull(get("source_url")),
      sourceHost: emptyToNull(get("source_host")),
      sourcePath: emptyToNull(get("source_path")),
      regexPattern: emptyToNull(get("regex_pattern")),
      destinationUrl,
      destinationMode,
      conditionPathContains: emptyToNull(get("condition_path_contains")),
      conditionPathPrefix: emptyToNull(get("condition_path_prefix")),
      conditionPathExcludes: emptyToNull(get("condition_path_excludes")),
      conditionQueryParamKey: emptyToNull(get("condition_query_param_key")),
      conditionQueryParamExact: emptyToNull(get("condition_query_param_exact")),
      conditionQueryParamValues: emptyToNull(
        get("condition_query_param_values")
      ),
      conditionQueryContains: emptyToNull(get("condition_query_contains")),
      conditionQueryExcludes: emptyToNull(get("condition_query_excludes")),
      conditionReferrerContains: emptyToNull(
        get("condition_referrer_contains")
      ),
      conditionTitleEquals: emptyToNull(get("condition_title_equals")),
      conditionEnvironments: emptyToNull(get("condition_environments")),
      conditionsJson: emptyToNull(get("conditions_json")),
    },
  };
};

export async function parseCsvToImportRows(
  file: File
): Promise<{ parsedRows: ParsedUrlMappingImportRow[]; errors: string[] }> {
  const text = await file.text();
  const parsed = Papa.parse<string[]>(text, { skipEmptyLines: true });
  const errors: string[] = [];

  if (parsed.errors.length > 0) {
    return { parsedRows: [], errors: ["CSVの解析に失敗しました"] };
  }

  const data = parsed.data;
  if (data.length === 0) {
    return { parsedRows: [], errors: ["CSVが空です"] };
  }

  const header = data[0].map(normalizeHeaderCell);
  const hasKnownHeader = header.includes("match_type");
  const body = hasKnownHeader ? data.slice(1) : data;

  const parsedRows: ParsedUrlMappingImportRow[] = [];
  body.forEach((cols, index) => {
    const lineNum = hasKnownHeader ? index + 2 : index + 1;
    const result = colsToRow(header, cols, lineNum);
    if ("error" in result) {
      errors.push(result.error);
      return;
    }

    parsedRows.push({
      lineNum,
      row: result.row,
      errors: validateImportRow(result.row, lineNum),
    });
  });

  if (parsedRows.length === 0 && errors.length === 0) {
    errors.push("インポート対象の行がありません");
  }

  if (parsedRows.length > IMPORT_MAX_URL_MAPPINGS) {
    errors.push(`最大${IMPORT_MAX_URL_MAPPINGS}件までインポートできます`);
  }

  return { parsedRows, errors };
}
